import { _, helpers, ts } from '_core';

import { SignalSetParams } from '@finsera/finsera/dist/resource/signal_set';

import { DraftParams } from './types';
import { mapName } from '../../common/utils/signal-set-utils';

export const validate = (params: DraftParams) => {
  const rhErrors = helpers.validations.validSelectedReturnHorizons(params?.return_horizons);
  if (!_.isEmpty(rhErrors)) return rhErrors;
  if (_.isEmpty(params.analysis_time_series_ids)) return ['You need to add at least one time series.'];
  if (params.analysis_time_series_ids.length > 3) return ['You can only add a max of three time series.'];
  return [];
};

export const prepare = (
  data: ts.types.widgets.TableData,
  resources: ts.StoreState['resources'],
  signalsObj: Record<string, Partial<SignalSetParams>>,
  selectedTimeSeries: ts.types.timeSeries.TimeSeries[]
): Record<string, ts.types.widgets.TableData> => {
  const result: Record<string, ts.types.widgets.TableData> = {};
  const entitiesMap: Record<string, Record<string, string>> = {}; // metric -> (key -> mappedEntity)

  data.forEach((entry) => {
    const { date, ...rest } = entry;

    _.forOwn(rest, (value, key) => {
      // This charts job is shared with other widgets that require period data like "1 Month_xxxx|1 Day Return"
      // On this one, as it is not needed, we need to skip these keys to avoid errors.
      if (key.includes('_')) return;

      const [entityKey, metric] = key.includes('|') ? key.split('|') : [key, key];

      const foundTs = selectedTimeSeries.find((ts) => ts.handle === entityKey);

      if (foundTs) {
        const label = helpers.resourcesUtils.getResourceLabel(foundTs);

        _.forEach(result, (tabEntries) => {
          let dateEntry = _.find(tabEntries, { date });

          if (!dateEntry) {
            dateEntry = { date };
            tabEntries.push(dateEntry);
          }

          dateEntry[label] = value;
        });
      } else {
        const { name: mappedEntity, handle } = mapName(entityKey, resources, signalsObj);

        if (!result[metric]) {
          result[metric] = [];
          entitiesMap[metric] = {};
        }

        // Check if we need to append handle to avoid duplicates
        let finalMappedEntity = mappedEntity;
        if (!entitiesMap[metric][key]) {
          const hasEntity = Object.values(entitiesMap[metric]).some((entity) => entity === mappedEntity);
          if (hasEntity) {
            finalMappedEntity = `${mappedEntity} (${handle})`;
          }
          entitiesMap[metric][key] = finalMappedEntity;
        } else {
          finalMappedEntity = entitiesMap[metric][key];
        }

        const dateEntry = _.find(result[metric], { date });

        if (dateEntry) {
          dateEntry[finalMappedEntity] = value;
        } else {
          result[metric].push({ date, [finalMappedEntity]: value });
        }
      }
    });
  });

  return result;
};
